import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import moment from "moment";

// Import các component mới
import {
  ExamHeader,
  ExamNavigation,
  ExamInfoPanel,
  ExamFixedInfoPanel,
  ExamQuestion,
  ExamModals,
} from "./components";
import { isNumeric } from "../../../common/Utils";
import useExamAnswers from "./useExamAnswers";

// Tạo storage key dựa trên examId và userId
const getStorageKey = (examId, assessmentId) => {
  return `exam_answers_${examId}_${assessmentId}`;
};

// Tạo storage key cho thời gian
const getTimeStorageKey = (examId, assessmentId) => {
  return `exam_time_${examId}_${assessmentId}`;
};

// Tạo storage key cho câu hỏi đánh dấu
const getMarkedQuestionsStorageKey = (examId, assessmentId) => {
  return `exam_marked_questions_${examId}_${assessmentId}`;
};

// Thêm hàm lưu và lấy current index vào sessionStorage
const getCurrentQuestionKey = (examId, assessmentId) =>
  `exam_current_${examId}_${assessmentId}`;

const saveCurrentToStorage = (examId, assessmentId, current) => {
  try {
    sessionStorage.setItem(
      getCurrentQuestionKey(examId, assessmentId),
      current.toString()
    );
  } catch (e) {}
};
const getCurrentFromStorage = (examId, assessmentId) => {
  try {
    const val = sessionStorage.getItem(
      getCurrentQuestionKey(examId, assessmentId)
    );
    if (val !== null) return parseInt(val, 10);
    return 0;
  } catch (e) {
    return 0;
  }
};

// Lưu đáp án vào sessionStorage
const saveAnswersToStorage = (examId, assessmentId, answers) => {
  try {
    const storageKey = getStorageKey(examId, assessmentId);
    const answersLocal = getAnswersFromStorage(examId, assessmentId);
    if (answersLocal) {
      sessionStorage.setItem(
        storageKey,
        JSON.stringify({ ...answersLocal, ...answers })
      );
    } else {
      sessionStorage.setItem(storageKey, JSON.stringify(answers));
    }
  } catch (error) {
    console.error("Error saving answers to sessionStorage:", error);
  }
};

// Lưu câu hỏi đánh dấu vào sessionStorage
const saveMarkedQuestionsToStorage = (
  examId,
  assessmentId,
  markedQuestions
) => {
  try {
    const markedQuestionsStorageKey = getMarkedQuestionsStorageKey(
      examId,
      assessmentId
    );
    sessionStorage.setItem(
      markedQuestionsStorageKey,
      JSON.stringify(markedQuestions)
    );
  } catch (error) {
    console.error("Error saving marked questions to sessionStorage:", error);
  }
};

// Lấy đáp án từ sessionStorage
const getAnswersFromStorage = (examId, assessmentId) => {
  try {
    const storageKey = getStorageKey(examId, assessmentId);
    const savedAnswers = sessionStorage.getItem(storageKey);
    if (savedAnswers) {
      return JSON.parse(savedAnswers);
    }
    return null;
  } catch (error) {
    console.error("Error getting answers from sessionStorage:", error);
    return null;
  }
};

// Lấy thời gian từ sessionStorage
const getTimeFromStorage = (examId, assessmentId) => {
  try {
    const timeStorageKey = getTimeStorageKey(examId, assessmentId);
    const savedTime = sessionStorage.getItem(timeStorageKey);
    if (savedTime) {
      return parseInt(savedTime, 10);
    }
    return null;
  } catch (error) {
    console.error("Error getting time from sessionStorage:", error);
    return null;
  }
};

// Lấy câu hỏi đánh dấu từ sessionStorage
const getMarkedQuestionsFromStorage = (examId, assessmentId) => {
  try {
    const markedQuestionsStorageKey = getMarkedQuestionsStorageKey(
      examId,
      assessmentId
    );
    const savedMarkedQuestions = sessionStorage.getItem(
      markedQuestionsStorageKey
    );
    if (savedMarkedQuestions) {
      return JSON.parse(savedMarkedQuestions);
    }
    return [];
  } catch (error) {
    console.error("Error getting marked questions from sessionStorage:", error);
    return [];
  }
};

function formatTime(sec) {
  const m = Math.floor(sec / 60)
    .toString()
    .padStart(2, "0");
  const s = (sec % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

const ViewExamPage = () => {
  const [searchParams] = useSearchParams();
  const subject = searchParams.get("subject");
  const { id } = useParams();
  const assessmentId = "123";
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [examData, setExamData] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [questionsMQ, setQuestionsMQ] = useState([]);
  const [answers, setInitialAnswers] = useState({});
  const [correctAnswers, setCorrectAnswers] = useState({});
  // Use centralized answer management
  const { answers1, updateAnswer, clearAnswers } = useExamAnswers(
    id,
    assessmentId,
    answers
  );
  // Mảng lưu trữ các câu hỏi được đánh dấu để làm sau
  const [markedQuestions, setMarkedQuestions] = useState([]);
  const [currentMQ, setCurrentMQ] = useState(0);
  const [current, setCurrent] = useState(() =>
    getCurrentFromStorage(id, assessmentId)
  );
  // Logic disabled cho navigation buttons - Smart navigation calculation
  const calculateNavigationDisabled = () => {
    const currentQuestion = questions[current];
    if (!currentQuestion) return { isDisabledNext: true, isDisabledBack: true };

    const currentQuestionNumber = parseInt(
      currentQuestion.question.match(/\d+/)?.[0] || "0"
    );

    // Lấy tất cả question numbers có sẵn và sort
    const allAvailableQuestionNumbers = questions
      .map((q) => parseInt(q.question.match(/\d+/)?.[0] || "0"))
      .filter((num) => !isNaN(num))
      .sort((a, b) => a - b);

    const sortedMQGroups = questionsMQ.sort((a, b) => a.range[0] - b.range[0]);

    // Check if current question is in MQ group
    const currentMQGroup = sortedMQGroups.find((group) =>
      group.range.includes(currentQuestionNumber)
    );

    // Calculate NEXT
    let hasNext = false;
    if (currentMQGroup) {
      // Trong MQ group - tìm MQ group tiếp theo
      const currentMQIndex = sortedMQGroups.findIndex(
        (group) => group === currentMQGroup
      );
      const nextMQGroup = sortedMQGroups[currentMQIndex + 1];

      if (nextMQGroup) {
        hasNext = true;
      } else {
        // Không có MQ group tiếp theo - check single questions sau range này
        const lastQuestionInCurrentRange = Math.max(...currentMQGroup.range);
        const nextQuestionNumbers = allAvailableQuestionNumbers.filter(
          (num) =>
            num > lastQuestionInCurrentRange &&
            !sortedMQGroups.some((group) => group.range.includes(num))
        );
        hasNext = nextQuestionNumbers.length > 0;
      }
    } else {
      // Single question - tìm target tiếp theo (MQ group hoặc single)
      const nextQuestionNumbers = allAvailableQuestionNumbers.filter(
        (num) => num > currentQuestionNumber
      );
      hasNext = nextQuestionNumbers.length > 0;
    }

    // Calculate BACK
    let hasBack = false;
    if (currentMQGroup) {
      // Trong MQ group - tìm MQ group trước đó
      const currentMQIndex = sortedMQGroups.findIndex(
        (group) => group === currentMQGroup
      );
      const prevMQGroup = sortedMQGroups[currentMQIndex - 1];

      if (prevMQGroup) {
        hasBack = true;
      } else {
        // Không có MQ group trước đó - check single questions trước range này
        const firstQuestionInCurrentRange = Math.min(...currentMQGroup.range);
        const prevQuestionNumbers = allAvailableQuestionNumbers.filter(
          (num) =>
            num < firstQuestionInCurrentRange &&
            !sortedMQGroups.some((group) => group.range.includes(num))
        );
        hasBack = prevQuestionNumbers.length > 0;
      }
    } else {
      // Single question - tìm target trước đó (MQ group hoặc single)
      const prevQuestionNumbers = allAvailableQuestionNumbers.filter(
        (num) => num < currentQuestionNumber
      );
      hasBack = prevQuestionNumbers.length > 0;
    }

    return {
      isDisabledNext: !hasNext,
      isDisabledBack: !hasBack,
    };
  };

  const { isDisabledNext, isDisabledBack } = calculateNavigationDisabled();
  const [showInfoPanel, setShowInfoPanel] = useState(false);
  const [showFullscreenModal, setShowFullscreenModal] = useState(false);
  const [shouldPromptFullscreen, setShouldPromptFullscreen] = useState(true);

  // State for showing explanation and video
  const [showExplanation, setShowExplanation] = useState({});
  const [showVideo, setShowVideo] = useState({});

  const handleFetch = async () => {
    try {
      setLoading(true);
      const examData = JSON.parse(localStorage.getItem("examData") || "{}");
      const answers = JSON.parse(localStorage.getItem("answers") || "{}");
      if (examData) {
        setExamData(examData);
        setQuestions(examData?.questions.filter((q) => q.type !== "MQ"));
        setQuestionsMQ(examData?.questions.filter((q) => q.type === "MQ"));

        // Kiểm tra xem có đáp án đã lưu trong sessionStorage không
        const savedAnswers = answers;
        if (savedAnswers) {
          clearAnswers();
          setInitialAnswers(savedAnswers);
          sessionStorage.setItem(
            getStorageKey(id, assessmentId),
            JSON.stringify(savedAnswers)
          );
        }
        // Lấy danh sách câu hỏi đã đánh dấu từ sessionStorage
        const savedMarkedQuestions = getMarkedQuestionsFromStorage(
          id,
          assessmentId
        );
        setMarkedQuestions(savedMarkedQuestions);

        // setInitialAnswers(initialAnswersObj);
      }
    } catch (error) {
      console.log("handleFetch ~ error:", error);
      const message = error?.response?.data?.message;
      toast.error(message);
      // navigate(`/courses/${error?.response?.data?.courseId}`);
    } finally {
      setLoading(false);
    }
  };

  // Hàm cập nhật currentMQ dựa trên câu hỏi hiện tại
  const updateCurrentMQFromCurrentQuestion = () => {
    const currentQuestion = questions[current];
    if (!currentQuestion) return;

    const currentQuestionNumber = parseInt(
      currentQuestion.question.match(/\d+/)?.[0] || "0"
    );

    // Kiểm tra xem câu hỏi có thuộc MQ group không
    const sortedMQGroups = questionsMQ.sort((a, b) => a.range[0] - b.range[0]);
    const mqGroup = sortedMQGroups.find((e) =>
      e?.range.includes(currentQuestionNumber)
    );

    if (mqGroup) {
      // Nếu thuộc MQ group, set currentMQ = question number
      setCurrentMQ(currentQuestionNumber);
    } else {
      // Nếu không thuộc MQ group (câu hỏi bình thường), set currentMQ = 0
      setCurrentMQ(0);
    }
  };

  useEffect(() => {
    handleFetch();
    // Khi examId hoặc assessmentId đổi, reset current về giá trị đã lưu (nếu có)
    const savedCurrent = getCurrentFromStorage(id, assessmentId);
    setCurrent(savedCurrent);
  }, [id, assessmentId]);

  // Cập nhật currentMQ khi current hoặc questions thay đổi
  useEffect(() => {
    if (questions.length > 0) {
      updateCurrentMQFromCurrentQuestion();
    }
  }, [current, questions, questionsMQ]);

  // Remove manual save logic - useExamAnswers handles this automatically

  // Lưu current vào sessionStorage mỗi khi current thay đổi
  useEffect(() => {
    saveCurrentToStorage(id, assessmentId, current);
  }, [current, id, assessmentId]);

  // useEffect KHÔNG gọi requestFullscreen trực tiếp nữa
  // useEffect(() => {
  //   const handleFullscreenChange = () => {
  //     const isFullscreen =
  //       document.fullscreenElement ||
  //       document.webkitFullscreenElement ||
  //       document.mozFullScreenElement ||
  //       document.msFullscreenElement;

  //     if (!isFullscreen) {
  //       setShowFullscreenModal(true);
  //     }
  //   };

  //   document.addEventListener("fullscreenchange", handleFullscreenChange);
  //   document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
  //   document.addEventListener("mozfullscreenchange", handleFullscreenChange);
  //   document.addEventListener("MSFullscreenChange", handleFullscreenChange);

  //   return () => {
  //     document.removeEventListener("fullscreenchange", handleFullscreenChange);
  //     document.removeEventListener(
  //       "webkitfullscreenchange",
  //       handleFullscreenChange
  //     );
  //     document.removeEventListener(
  //       "mozfullscreenchange",
  //       handleFullscreenChange
  //     );
  //     document.removeEventListener(
  //       "MSFullscreenChange",
  //       handleFullscreenChange
  //     );
  //   };
  // }, []);

  const handleBackToFullscreen = () => {
    setShowFullscreenModal(false);
    const el = document.documentElement;
    if (el.requestFullscreen) el.requestFullscreen();
    else if (el.mozRequestFullScreen) el.mozRequestFullScreen();
    else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
    else if (el.msRequestFullscreen) el.msRequestFullscreen();
  };

  // Helper function để check xem câu hỏi hiện tại có thuộc MQ range không
  const isCurrentQuestionMQ = () => {
    const currentQuestion = questions[current];
    if (!currentQuestion) return false;

    const currentQuestionNumber = parseInt(
      currentQuestion.question.match(/\d+/)?.[0] || "0"
    );

    const sortedMQGroups = questionsMQ.sort((a, b) => a.range[0] - b.range[0]);

    return sortedMQGroups.some((group) =>
      group.range.includes(currentQuestionNumber)
    );
  };

  // useEffect(() => {
  //   const handleKeyEvent = (e) => {
  //     // Print Screen (keyCode 44)
  //     if (e.keyCode === 44) {
  //       handleScreenshotAttempt();
  //       return;
  //     }

  //     // Alt + Print Screen
  //     if (e.altKey && e.keyCode === 44) {
  //       handleScreenshotAttempt();
  //       return;
  //     }

  //     // Windows + Shift + S (Snipping Tool)
  //     if (e.metaKey && e.shiftKey && e.key === "S") {
  //       e.preventDefault();
  //       handleScreenshotAttempt();
  //       return;
  //     }

  //     // Ctrl + Shift + I (Developer Tools)
  //     if (e.ctrlKey && e.shiftKey && e.keyCode === 73) {
  //       e.preventDefault();
  //       handleScreenshotAttempt();
  //       return;
  //     }
  //   };

  //   const handleScreenshotAttempt = () => {
  //     const effects = [
  //       // createRedWarningOverlay,
  //       // createBlackScreenOverlay,
  //       createBlurOverlay,
  //     ];

  //     // Chọn ngẫu nhiên một hiệu ứng
  //     const randomEffect = effects[Math.floor(Math.random() * effects.length)];
  //     randomEffect();
  //   };

  //   //   const createRedWarningOverlay = () => {
  //   //     const overlay = document.createElement("div");
  //   //     overlay.id = "screenshot-blocker";
  //   //     overlay.style.cssText = `
  //   //   position: fixed;
  //   //   top: 0;
  //   //   left: 0;
  //   //   width: 100vw;
  //   //   height: 100vh;
  //   //   background: #ff0000;
  //   //   z-index: 999999;
  //   //   display: flex;
  //   //   align-items: center;
  //   //   justify-content: center;
  //   //   animation: flashRed 0.2s infinite;
  //   // `;

  //   //     overlay.innerHTML = `
  //   //   <div style="color: white; font-size: 60px; font-weight: bold; text-align: center;">
  //   //     🚫 CHỤP MÀN HÌNH BỊ CHẶN 🚫
  //   //   </div>
  //   // `;

  //   //     addOverlayStyles();
  //   //     document.body.appendChild(overlay);
  //   //     removeOverlayAfterDelay(overlay);
  //   //   };

  //   //   const createBlackScreenOverlay = () => {
  //   //     const overlay = document.createElement("div");
  //   //     overlay.id = "screenshot-blocker";
  //   //     overlay.style.cssText = `
  //   //   position: fixed;
  //   //   top: 0;
  //   //   left: 0;
  //   //   width: 100vw;
  //   //   height: 100vh;
  //   //   background: #000000;
  //   //   z-index: 999999;
  //   //   display: flex;
  //   //   align-items: center;
  //   //   justify-content: center;
  //   // `;

  //   //     overlay.innerHTML = `
  //   //   <div style="color: #ff0000; font-size: 40px; font-weight: bold; text-align: center; animation: pulse 1s infinite;">
  //   //     ⚠️ NỘI DUNG ĐƯỢC BẢO VỆ ⚠️<br>
  //   //     <div style="font-size: 20px; margin-top: 20px; color: white;">
  //   //       Không thể chụp màn hình
  //   //     </div>
  //   //   </div>
  //   // `;

  //   //     addOverlayStyles();
  //   //     document.body.appendChild(overlay);
  //   //     removeOverlayAfterDelay(overlay);
  //   //   };

  //   const createBlurOverlay = () => {
  //     // Làm mờ toàn bộ body
  //     document.body.style.filter = "blur(20px)";
  //     document.body.style.transition = "filter 0.1s";

  //     // Ngăn scroll
  //     document.body.style.overflow = "hidden";

  //     const overlay = document.createElement("div");
  //     overlay.id = "screenshot-blocker";
  //     overlay.style.cssText = `
  //   position: fixed;
  //   top: 0;
  //   left: 0;
  //   width: 100vw;
  //   height: 100vh;
  //   background: rgba(255, 255, 255, 0.9);
  //   z-index: 999999;
  //   display: flex;
  //   align-items: center;
  //   justify-content: center;
  //   overflow: hidden;
  //   margin: 0;
  //   padding: 0;
  // `;

  //     overlay.innerHTML = `
  //   <div style="color: #ff0000; font-size: 50px; font-weight: bold; text-align: center;">
  //     🔒 NỘI DUNG BỊ CHE 🔒
  //   </div>
  // `;

  //     document.body.appendChild(overlay);

  //     setTimeout(() => {
  //       document.body.style.filter = "none";
  //       document.body.style.overflow = ""; // Khôi phục scroll
  //       if (document.getElementById("screenshot-blocker")) {
  //         document.body.removeChild(overlay);
  //       }
  //     }, 3000);
  //   };

  //   const addOverlayStyles = () => {
  //     if (!document.getElementById("overlay-styles")) {
  //       const style = document.createElement("style");
  //       style.id = "overlay-styles";
  //       style.textContent = `
  //     @keyframes flashRed {
  //       0% { background: #ff0000; }
  //       50% { background: #990000; }
  //       100% { background: #ff0000; }
  //     }

  //     @keyframes pulse {
  //       0% { transform: scale(1); opacity: 1; }
  //       50% { transform: scale(1.1); opacity: 0.8; }
  //       100% { transform: scale(1); opacity: 1; }
  //     }

  //     @keyframes staticNoise {
  //       0% { opacity: 1; }
  //       25% { opacity: 0.8; }
  //       50% { opacity: 0.6; }
  //       75% { opacity: 0.9; }
  //       100% { opacity: 1; }
  //     }
  //   `;
  //       document.head.appendChild(style);
  //     }
  //   };

  //   const removeOverlayAfterDelay = (overlay, delay = 3000) => {
  //     setTimeout(() => {
  //       if (document.getElementById("screenshot-blocker")) {
  //         document.body.removeChild(overlay);
  //       }
  //     }, delay);
  //   };

  //   document.addEventListener("keydown", handleKeyEvent);
  //   document.addEventListener("keyup", handleKeyEvent);

  //   return () => {
  //     document.removeEventListener("keydown", handleKeyEvent);
  //     document.removeEventListener("keyup", handleKeyEvent);

  //     // Cleanup overlay nếu component unmount
  //     const overlay = document.getElementById("screenshot-blocker");
  //     if (overlay) {
  //       document.body.removeChild(overlay);
  //     }
  //   };
  // }, [id, assessmentId, current]);

  const handleSelect = (value, sub, q) => {
    if (!q || !q.question) {
      console.error("Invalid question object:", q);
      return;
    }

    // Ngăn chặn scroll khi đang xử lý answer
    const currentScrollTop =
      window.pageYOffset || document.documentElement.scrollTop;

    const questionNumber = q.question;

    switch (q.type) {
      case "TN":
        updateAnswer(questionNumber, value);
        break;
      case "MA":
        const currentAnswers = answers[questionNumber] || [];
        const newAnswers = currentAnswers.includes(value)
          ? currentAnswers.filter((a) => a !== value)
          : [...currentAnswers, value];
        updateAnswer(questionNumber, newAnswers);
        break;
      case "DS":
        const currentDSAnswer = answers[questionNumber] || {};
        updateAnswer(questionNumber, {
          ...currentDSAnswer,
          ...value,
        });
        break;
      case "TLN":
        // Parse number if possible, otherwise keep as string
        let numericValue = value;
        if (typeof value === "string" && value.includes(",")) {
          numericValue = numericValue.replace(",", ".");
        }

        if (isNumeric(numericValue)) {
          numericValue = parseFloat(numericValue);
          updateAnswer(questionNumber, numericValue);
        } else {
          updateAnswer(questionNumber, value);
        }

        break;
      case "TLN_M":
        {
          // Parse number if possible, otherwise keep as string
          let numericValue = value;

          if (typeof value === "string" && value.includes(",")) {
            numericValue = numericValue.replace(",", ".");
          }

          const currentTLNMAnswer = answers[questionNumber] || {};
          if (isNumeric(numericValue)) {
            numericValue = parseFloat(numericValue);
            updateAnswer(questionNumber, {
              ...currentTLNMAnswer,
              [sub.key]: numericValue,
            });
          } else {
            updateAnswer(questionNumber, {
              ...currentTLNMAnswer,
              [sub.key]: value,
            });
          }
        }
        break;
      default:
        break;
    }

    // Khôi phục scroll position sau khi xử lý
    setTimeout(() => {
      window.scrollTo(0, currentScrollTop);
    }, 0);
  };

  // Hàm scroll an toàn không gây vệt trắng
  const scrollToQuestion = (questionIndex) => {
    // Lấy question để check số thứ tự
    const targetQuestion = questions[questionIndex];
    if (!targetQuestion) return;

    const questionNumber = parseInt(
      targetQuestion.question.match(/\d+/)?.[0] || "0"
    );
    const currentQuestionMQ = questionsMQ.find((e) =>
      e?.range.includes(questionNumber)
    );

    if (currentQuestionMQ) {
      // Scroll cho MQ questions
      setTimeout(() => {
        const element = document.getElementById(`question-${questionIndex}`);
        if (element) {
          const container = element.closest(".overflow-y-auto");

          if (container) {
            const containerRect = container.getBoundingClientRect();
            const elementRect = element.getBoundingClientRect();

            const isAbove = elementRect.top < containerRect.top;
            const isBelow = elementRect.bottom > containerRect.bottom;

            if (isAbove || isBelow) {
              const scrollTopDelta =
                element.offsetTop -
                container.offsetTop -
                container.clientHeight / 3;

              container.scrollTo({
                top: scrollTopDelta,
                behavior: "smooth", // Vẫn giữ mượt nhưng không phụ thuộc scrollIntoView
              });
            }
          }
        }
      }, 100);
    }
    // Non-MQ questions không cần scroll vì chúng fullscreen
  };

  // Hàm điều hướng cho nút Previous/Next - Navigation theo range hoặc từng câu
  const handleNav = (idx, isNext) => {
    const currentQuestion = questions[current];
    if (!currentQuestion) return;

    const currentQuestionNumber = parseInt(
      currentQuestion.question.match(/\d+/)?.[0] || "0"
    );

    // Tìm tất cả các MQ groups và sắp xếp theo thứ tự
    const sortedMQGroups = questionsMQ.sort((a, b) => a.range[0] - b.range[0]);

    // Kiểm tra câu hỏi hiện tại có thuộc MQ group không
    const currentMQGroup = sortedMQGroups.find((e) =>
      e?.range.includes(currentQuestionNumber)
    );

    if (isNext) {
      let targetQuestionNumber;

      if (currentMQGroup) {
        // Câu hiện tại thuộc MQ group - chuyển sang range tiếp theo
        const currentMQIndex = sortedMQGroups.findIndex(
          (group) => group === currentMQGroup
        );
        const nextMQGroup = sortedMQGroups[currentMQIndex + 1];

        if (nextMQGroup) {
          // Có MQ group tiếp theo - chuyển tới câu đầu tiên của range đó
          targetQuestionNumber = nextMQGroup.range[0];
        } else {
          // Không có MQ group tiếp theo - tìm câu hỏi đơn lẻ tiếp theo
          const lastQuestionInCurrentRange = Math.max(...currentMQGroup.range);
          const allQuestionNumbers = questions
            .map((q) => parseInt(q.question.match(/\d+/)?.[0] || "0"))
            .sort((a, b) => a - b);

          targetQuestionNumber = allQuestionNumbers.find(
            (num) =>
              num > lastQuestionInCurrentRange &&
              !sortedMQGroups.some((group) => group.range.includes(num))
          );
        }
      } else {
        // Câu hiện tại không thuộc MQ group - chuyển từng câu đơn lẻ
        const allQuestionNumbers = questions
          .map((q) => parseInt(q.question.match(/\d+/)?.[0] || "0"))
          .sort((a, b) => a - b);

        // Tìm câu đơn lẻ tiếp theo trước
        const nextSingleQuestion = allQuestionNumbers.find(
          (num) =>
            num > currentQuestionNumber &&
            !sortedMQGroups.some((group) => group.range.includes(num))
        );

        if (nextSingleQuestion) {
          // Có câu đơn lẻ tiếp theo - chuyển tới câu đó
          targetQuestionNumber = nextSingleQuestion;
        } else {
          // Không có câu đơn lẻ tiếp theo - tìm MQ group tiếp theo
          const nextMQGroup = sortedMQGroups.find(
            (group) => group.range[0] > currentQuestionNumber
          );
          if (nextMQGroup) {
            targetQuestionNumber = nextMQGroup.range[0];
          }
        }
      }

      // Tìm index của target question và chuyển tới
      if (targetQuestionNumber) {
        const targetIndex = questions.findIndex(
          (q) =>
            parseInt(q.question.match(/\d+/)?.[0] || "0") ===
            targetQuestionNumber
        );

        if (targetIndex !== -1 && targetIndex < questions.length) {
          setCurrent(targetIndex);

          const targetMQGroup = sortedMQGroups.find((e) =>
            e?.range.includes(targetQuestionNumber)
          );

          if (targetMQGroup) {
            setCurrentMQ(targetQuestionNumber);
          } else {
            setCurrentMQ(0);
          }

          scrollToQuestion(targetIndex);
        }
      }
    } else {
      // Back: logic tương tự nhưng ngược lại
      let targetQuestionNumber;

      if (currentMQGroup) {
        // Câu hiện tại thuộc MQ group - chuyển sang range trước đó
        const currentMQIndex = sortedMQGroups.findIndex(
          (group) => group === currentMQGroup
        );
        const prevMQGroup = sortedMQGroups[currentMQIndex - 1];

        if (prevMQGroup) {
          // Có MQ group trước đó - chuyển tới câu đầu tiên của range đó
          targetQuestionNumber = prevMQGroup.range[0];
        } else {
          // Không có MQ group trước đó - tìm câu hỏi đơn lẻ trước đó
          const firstQuestionInCurrentRange = Math.min(...currentMQGroup.range);
          const allQuestionNumbers = questions
            .map((q) => parseInt(q.question.match(/\d+/)?.[0] || "0"))
            .sort((a, b) => b - a); // Sort descending để tìm câu gần nhất

          targetQuestionNumber = allQuestionNumbers.find(
            (num) =>
              num < firstQuestionInCurrentRange &&
              !sortedMQGroups.some((group) => group.range.includes(num))
          );
        }
      } else {
        // Câu hiện tại không thuộc MQ group - chuyển từng câu đơn lẻ
        const allQuestionNumbers = questions
          .map((q) => parseInt(q.question.match(/\d+/)?.[0] || "0"))
          .sort((a, b) => b - a); // Sort descending

        // Tìm câu đơn lẻ trước đó trước
        const prevSingleQuestion = allQuestionNumbers.find(
          (num) =>
            num < currentQuestionNumber &&
            !sortedMQGroups.some((group) => group.range.includes(num))
        );

        if (prevSingleQuestion) {
          // Có câu đơn lẻ trước đó - chuyển tới câu đó
          targetQuestionNumber = prevSingleQuestion;
        } else {
          // Không có câu đơn lẻ trước đó - tìm MQ group trước đó
          const prevMQGroup = [...sortedMQGroups]
            .reverse()
            .find((group) => group.range[0] < currentQuestionNumber);
          if (prevMQGroup) {
            targetQuestionNumber = prevMQGroup.range[0];
          }
        }
      }

      // Tìm index của target question và chuyển tới
      if (targetQuestionNumber) {
        const targetIndex = questions.findIndex(
          (q) =>
            parseInt(q.question.match(/\d+/)?.[0] || "0") ===
            targetQuestionNumber
        );

        if (targetIndex !== -1 && targetIndex >= 0) {
          setCurrent(targetIndex);

          const targetMQGroup = sortedMQGroups.find((e) =>
            e?.range.includes(targetQuestionNumber)
          );

          if (targetMQGroup) {
            setCurrentMQ(targetQuestionNumber);
          } else {
            setCurrentMQ(0);
          }

          scrollToQuestion(targetIndex);
        }
      }
    }
  };

  // Hàm điều hướng từ Info Panel
  const handleNavFromPanel = (idx) => {
    // Lấy câu hỏi tại index được click
    const clickedQuestion = questions[idx];
    if (!clickedQuestion) return;

    // Lấy số thứ tự câu hỏi
    const questionNumber = parseInt(
      clickedQuestion.question.match(/\d+/)?.[0] || "0"
    );

    // Tìm tất cả các MQ groups
    const sortedMQGroups = questionsMQ.sort((a, b) => a.range[0] - b.range[0]);

    // Kiểm tra xem câu hỏi này có thuộc MQ group nào không
    const mqGroup = sortedMQGroups.find((e) =>
      e?.range.includes(questionNumber)
    );

    if (mqGroup) {
      // Nếu thuộc MQ group, set currentMQ về đúng câu hỏi được click
      setCurrentMQ(questionNumber);
      setCurrent(idx); // Set current về đúng index được click
      scrollToQuestion(idx);
    } else {
      // Nếu không thuộc MQ group, đây là câu hỏi bình thường
      setCurrent(idx);
      setCurrentMQ(0); // Reset currentMQ để đảm bảo không hiển thị UI MQ
      scrollToQuestion(idx);
    }
  };

  const handleSubmit = async () => {
    // TODO: Implement exam submission
    try {
      let savedAnswers = getAnswersFromStorage(id, assessmentId);

      // Calculate the time used
      const startTime = moment(examData.start);
      const examCompledTime = sessionStorage.getItem(
        `exam_time_${id}_${assessmentId}`
      );
      const usedDurationMs =
        (JSON.parse(examCompledTime) || 0) - startTime.valueOf();
      const usedMinutes = Math.floor(usedDurationMs);

      // Clear answers using hook
      clearAnswers();

      const timeStorageKey = getTimeStorageKey(id, assessmentId);
      const markedQuestionsStorageKey = getMarkedQuestionsStorageKey(
        id,
        assessmentId
      );
      sessionStorage.removeItem(timeStorageKey);
      sessionStorage.removeItem(markedQuestionsStorageKey);
      sessionStorage.removeItem(`exam_time_${id}_${assessmentId}`);
      sessionStorage.removeItem(
        `current_time_${id}_${assessmentId}_${current}`
      );
      sessionStorage.removeItem(`exam_current_${id}_${assessmentId}`);
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const key = sessionStorage.key(i);
        if (key.startsWith("current_time_")) {
          sessionStorage.removeItem(key);
        }
      }
      // const examKey = `exams_${assessmentId}`;
      // const response = await getUserExamProgress(examKey);
      // const exams = response?.data?.exams || [];
      // if (exams.length === 0) {
      //   await updateUserExamProgressCompleted(examKey);
      //   navigate(
      //     `/exam/result/${assessmentId}?examRoomId=${examRoomId}&uniqueId=${res.uniqueId}`
      //   );
      // } else {
      //   navigate(`/assessment/${assessmentId}?examRoomId=${examRoomId}`);
      // }
      navigate(`/exam/result/${assessmentId}`);
    } catch (error) {
      const message = error?.response?.data?.message || error.message;
      toast.error(message);
    }
  };

  // Hàm kiểm tra xem câu hỏi đã được trả lời hay chưa
  const isQuestionAnswered = (question) => {
    if (!question || !answers[question.question]) {
      return false;
    }

    const answer = answers[question.question];
    let isAnswered = false;

    switch (question.type) {
      case "TN":
        // Trắc nghiệm: đã chọn một đáp án (A, B, C, D)
        isAnswered = answer !== null && answer !== undefined;
        break;

      case "MA":
        // Nhiều lựa chọn: đã chọn ít nhất một đáp án
        isAnswered = Array.isArray(answer) && answer.length > 0;
        break;

      case "DS":
        // Đúng/Sai: đã chọn ít nhất một mệnh đề
        isAnswered =
          typeof answer === "object" &&
          Object.keys(answer).length > 0 &&
          Object.values(answer).some((val) => val === "D" || val === "S");
        break;

      case "TLN":
        // Trả lời ngắn: đã nhập câu trả lời (không rỗng)
        isAnswered = answer !== null && answer !== undefined && answer !== "";
        break;

      case "TLN_M":
        // Trả lời ngắn nhiều mệnh đề: đã nhập ít nhất một câu trả lời
        if (typeof answer !== "object") {
          isAnswered = false;
        } else {
          isAnswered = Object.values(answer).some((val) => {
            // Kiểm tra giá trị hợp lệ, bỏ qua các giá trị rỗng
            return val !== null && val !== undefined && val !== "";
          });
        }
        break;

      case "KT":
        // Kéo thả: đã kéo ít nhất một item vào slot
        if (typeof answer !== "object") {
          isAnswered = false;
        } else {
          isAnswered = Object.values(answer).some(
            (val) => val !== null && val !== undefined
          );
        }
        break;

      default:
        isAnswered = false;
    }

    return isAnswered;
  };

  // Số câu hỏi đã trả lời
  const doneCount = questions.filter((q) => isQuestionAnswered(q)).length;

  const onDragEnd = (question) => (result) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;

    const questionNumber = question?.question;

    // Nếu vị trí nguồn và đích giống nhau, không làm gì cả
    if (destination.droppableId === source.droppableId) {
      return;
    }

    updateAnswer((prev) => {
      const currentAnswers = prev[questionNumber] || {};
      const updatedAnswers = { ...currentAnswers };

      // Nếu kéo từ slot này sang slot khác
      if (
        source.droppableId.startsWith("slot") &&
        destination.droppableId.startsWith("slot")
      ) {
        // Xóa item khỏi slot cũ
        updatedAnswers[source.droppableId] = null;
        // Thêm item vào slot mới
        updatedAnswers[destination.droppableId] = draggableId;
      }
      // Nếu kéo từ hàng items xuống slot
      else if (
        source.droppableId === "items" &&
        destination.droppableId.startsWith("slot")
      ) {
        updatedAnswers[destination.droppableId] = draggableId;
      }
      // Nếu kéo từ slot lên hàng items
      else if (
        source.droppableId.startsWith("slot") &&
        destination.droppableId === "items"
      ) {
        updatedAnswers[source.droppableId] = null;
      }

      const newState = {
        ...prev,
        [questionNumber]: updatedAnswers,
      };

      // Lưu ngay đáp án sau khi kéo thả
      saveAnswersToStorage(id, assessmentId, newState);

      return newState;
    });
  };

  // Hàm toggle hiển thị lời giải
  const toggleExplanation = (questionKey) => {
    setShowExplanation((prev) => ({
      ...prev,
      [questionKey]: !prev[questionKey],
    }));
  };

  // Hàm toggle hiển thị video
  const toggleVideo = (questionKey) => {
    setShowVideo((prev) => ({
      ...prev,
      [questionKey]: !prev[questionKey],
    }));
  };

  // Hàm để đánh dấu hoặc bỏ đánh dấu câu hỏi
  const toggleMarkQuestion = (idx) => {
    setMarkedQuestions((prev) => {
      let newMarkedQuestions;
      if (prev.includes(idx)) {
        // Nếu câu hỏi đã được đánh dấu, bỏ đánh dấu
        newMarkedQuestions = prev.filter((q) => q !== idx);
        // toast.info(`Đã bỏ đánh dấu câu ${idx + 1}`, {
        //   position: "top-right",
        //   autoClose: 2000,
        //   hideProgressBar: true,
        //   icon: "🔖",
        // });
      } else {
        // Nếu câu hỏi chưa được đánh dấu, thêm vào danh sách
        newMarkedQuestions = [...prev, idx];
        // toast.success(`Đã đánh dấu câu ${idx + 1} để làm sau`, {
        //   position: "top-right",
        //   autoClose: 2000,
        //   hideProgressBar: true,
        //   icon: "🔖",
        // });
      }

      // Lưu danh sách đánh dấu vào sessionStorage
      saveMarkedQuestionsToStorage(id, assessmentId, newMarkedQuestions);

      return newMarkedQuestions;
    });
  };

  return (
    <div className="h-screen flex flex-col bg-[#f8f9fa] exam-no-select">
      {/* Header */}
      <ExamHeader examData={examData} />
      {/* Main Content */}
      <div className="flex-1 flex gap-0 max-w-full mx-auto w-full relative overflow-hidden h-[calc(100vh-64px)]">
        {/* Main Question Area */}
        <div
          className="flex flex-col flex-1 px-2 py-3 overflow-hidden md:px-3 md:py-4"
          style={{ scrollBehavior: "auto" }}
        >
          <ExamQuestion
            id={id}
            assessmentId={assessmentId}
            questions={questions}
            questionsMQ={questionsMQ}
            current={current}
            currentMQ={currentMQ}
            answers={answers}
            markedQuestions={markedQuestions}
            toggleMarkQuestion={toggleMarkQuestion}
            handleSelect={handleSelect}
            onDragEnd={onDragEnd}
            isQuestionAnswered={isQuestionAnswered}
            onAnswerChange={updateAnswer}
            showExplanation={showExplanation}
            showVideo={showVideo}
            toggleExplanation={toggleExplanation}
            toggleVideo={toggleVideo}
          />

          {/* Navigation Footer */}
          <ExamNavigation
            currentMQ={currentMQ}
            questionsMQ={questionsMQ}
            current={current}
            isDisabledBack={isDisabledBack}
            isDisabledNext={isDisabledNext}
            handleNav={handleNav}
            examData={examData}
            assessmentId={assessmentId}
            handleSubmit={handleSubmit}
            setShowInfoPanel={setShowInfoPanel}
            showInfoPanel={showInfoPanel}
            isCurrentQuestionMQ={isCurrentQuestionMQ}
            correctAnswers={correctAnswers}
          />

          {/* Slide-out Info Panel */}
          <ExamInfoPanel
            showInfoPanel={showInfoPanel}
            setShowInfoPanel={setShowInfoPanel}
            examData={examData}
            assessmentId={assessmentId}
            handleSubmit={handleSubmit}
            questions={questions}
            current={current}
            currentMQ={currentMQ}
            questionsMQ={questionsMQ}
            isQuestionAnswered={isQuestionAnswered}
            markedQuestions={markedQuestions}
            handleNavFromPanel={handleNavFromPanel}
            doneCount={doneCount}
            isCurrentQuestionMQ={isCurrentQuestionMQ}
          />
        </div>

        {/* Fixed Info Panel - chỉ hiện khi không phải câu hỏi MQ */}
        <ExamFixedInfoPanel
          currentMQ={currentMQ}
          questionsMQ={questionsMQ}
          examData={examData}
          assessmentId={assessmentId}
          handleSubmit={handleSubmit}
          questions={questions}
          current={current}
          isQuestionAnswered={isQuestionAnswered}
          markedQuestions={markedQuestions}
          handleNavFromPanel={handleNavFromPanel}
          doneCount={doneCount}
          isCurrentQuestionMQ={isCurrentQuestionMQ}
        />
      </div>
      {/* Modals
      <ExamModals
        showFullscreenModal={showFullscreenModal}
        shouldPromptFullscreen={shouldPromptFullscreen}
        setShouldPromptFullscreen={setShouldPromptFullscreen}
        handleBackToFullscreen={handleBackToFullscreen}
      /> */}
    </div>
  );
};

export default ViewExamPage;
